import { css } from 'aphrodite';
import { saveAs } from 'file-saver';
import * as React from 'react';
import { DownloadIconV2 } from '../../components/svgs/icons/DownloadIconV2';
import { MinusIcon } from '../../components/svgs/icons/MinusIcon';
import { PlusIcon2 } from '../../components/svgs/icons/PlusIcon';
import { XIconNew } from '../../components/svgs/icons/XIcon';
import { Button } from '../Button';
import { Dialog, DialogBody, DialogClose, DialogHeader, DialogPopup, DialogTitle } from '../Dialog';
import { styles } from './styles';

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.25;

export type PreviewMediaKind = 'image' | 'audio' | 'video';

export interface MediaPreviewDialogProps {
	alt: string;
	fileName?: string;
	/** Images get the zoom controls; audio and video get a player that starts on open. */
	kind?: PreviewMediaKind;
	/**
	 * The currently valid url for the same media, when `src` is a signed url that can expire while the
	 * dialog is open. Used to retry playback once and to download, never to re-point a playing element.
	 */
	latestSrc?: string;
	open: boolean;
	src: string;
	title?: string;
	onOpenChange: (open: boolean) => void;
}

export const MediaPreviewDialog: React.FC<MediaPreviewDialogProps> = ({
	alt,
	fileName,
	kind = 'image',
	latestSrc,
	open,
	src,
	title,
	onOpenChange,
}) => {
	const [zoom, setZoom] = React.useState(1);
	const [playbackFailed, setPlaybackFailed] = React.useState(false);
	const [playerSrc, setPlayerSrc] = React.useState(src);
	const playbackPositionRef = React.useRef(0);
	const retriedRef = React.useRef(false);
	const resolvedFileName = fileName || getFileNameFromSrc(src);
	const isImage = kind === 'image';

	React.useEffect(() => {
		setZoom(1);
		setPlaybackFailed(false);
		setPlayerSrc(src);
		playbackPositionRef.current = 0;
		retriedRef.current = false;
	}, [open, src]);

	const changeZoom = (amount: number) => {
		setZoom(current => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, current + amount)));
	};

	const handleTimeUpdate = (event: React.SyntheticEvent<HTMLMediaElement>) => {
		playbackPositionRef.current = event.currentTarget.currentTime;
	};

	const handleLoadedMetadata = (event: React.SyntheticEvent<HTMLMediaElement>) => {
		if (playbackPositionRef.current > 0) event.currentTarget.currentTime = playbackPositionRef.current;
	};

	// A signed url expires in ten minutes, which a long call recording outlives: the range request for the
	// unbuffered tail 403s mid-playback. `latestSrc` is re-signed on every poll, so spend one retry on it
	// and resume where the old url died before giving up.
	const handlePlaybackError = () => {
		if (!retriedRef.current && latestSrc && latestSrc !== playerSrc) {
			retriedRef.current = true;
			setPlayerSrc(latestSrc);
			return;
		}
		setPlaybackFailed(true);
	};

	const download = () => saveAs(latestSrc || src, resolvedFileName);

	return (
		<Dialog open={open} onOpenChange={onOpenChange} dismissOnOutsideClick>
			<DialogPopup styles={[styles.popup]} backdropStyles={[styles.backdrop]} showCloseButton={false}>
				<DialogHeader styles={[styles.toolbar]}>
					<div className={css(styles.toolbarIdentity)}>
						<DialogClose
							render={
								<Button
									type='button'
									variant='ghost'
									size='icon'
									styles={[styles.toolbarButton]}
									aria-label={`Close ${kind} preview`}
									title={`Close ${kind} preview`}
								/>
							}
						>
							<XIconNew />
						</DialogClose>
						<DialogTitle styles={[styles.title]}>{title || resolvedFileName || kind}</DialogTitle>
					</div>
					<Button
						type='button'
						variant='ghost'
						size='icon'
						styles={[styles.toolbarButton]}
						onClick={download}
						aria-label={`Download ${kind}`}
						title={`Download ${kind}`}
					>
						<DownloadIconV2 />
					</Button>
				</DialogHeader>
				<DialogBody
					styles={[
						styles.stage,
						...(isImage ? [] : [styles.mediaStage]),
						...(isImage && zoom > 1 ? [styles.stageZoomed] : []),
					]}
					onClick={event => {
						if (event.target === event.currentTarget) onOpenChange(false);
					}}
				>
					{playbackFailed ? (
						<div className={css(styles.playbackError)}>
							<p className={css(styles.playbackErrorText)}>
								This {kind} can&apos;t be played in the browser. Download it to open it in another app.
							</p>
							<Button type='button' variant='outline' size='sm' onClick={download}>
								Download {resolvedFileName || kind}
							</Button>
						</div>
					) : (
						<>
							{isImage && (
								<img
									className={css(styles.image, zoom > 1 && styles.imageZoomed)}
									src={src}
									alt={alt}
									draggable={false}
									style={{ transform: `scale(${zoom})` }}
								/>
							)}
							{kind === 'video' && (
								<video
									className={css(styles.video)}
									src={playerSrc}
									controls
									autoPlay
									playsInline
									aria-label={alt}
									onTimeUpdate={handleTimeUpdate}
									onLoadedMetadata={handleLoadedMetadata}
									onError={handlePlaybackError}
								>
									<track kind='captions' />
								</video>
							)}
							{kind === 'audio' && (
								<div className={css(styles.audioCard)}>
									<span className={css(styles.audioCardName)}>{resolvedFileName}</span>
									<audio
										className={css(styles.audioPlayer)}
										src={playerSrc}
										controls
										autoPlay
										aria-label={alt}
										onTimeUpdate={handleTimeUpdate}
										onLoadedMetadata={handleLoadedMetadata}
										onError={handlePlaybackError}
									>
										<track kind='captions' />
									</audio>
								</div>
							)}
						</>
					)}
					{isImage && !playbackFailed && (
						<div className={css(styles.zoomControls)}>
							<Button
								type='button'
								variant='ghost'
								size='icon'
								styles={[styles.zoomButton]}
								onClick={() => changeZoom(-ZOOM_STEP)}
								disabled={zoom <= MIN_ZOOM}
								aria-label='Zoom out'
								title='Zoom out'
							>
								<MinusIcon />
							</Button>
							<span className={css(styles.zoomLevel)} aria-live='polite'>
								{Math.round(zoom * 100)}%
							</span>
							<Button
								type='button'
								variant='ghost'
								size='icon'
								styles={[styles.zoomButton]}
								onClick={() => changeZoom(ZOOM_STEP)}
								disabled={zoom >= MAX_ZOOM}
								aria-label='Zoom in'
								title='Zoom in'
							>
								<PlusIcon2 />
							</Button>
						</div>
					)}
				</DialogBody>
			</DialogPopup>
		</Dialog>
	);
};

// Undefined rather than a placeholder: `saveAs` derives the name from the url and its headers when it
// gets none, which beats saving an inline email image as an extensionless file called "image".
function getFileNameFromSrc(src: string): string | undefined {
	try {
		const fileName = new URL(src, window.location.href).pathname.split('/').filter(Boolean).pop();
		return fileName ? decodeURIComponent(fileName) : undefined;
	} catch {
		return undefined;
	}
}
